import { debounce } from 'lodash-es';
/** Schedules an abortable function with an optional leading and always-on trailing debounce. */
export class DebouncedScheduler {
    fn;
    invoke;
    pending;
    constructor({ fn, waitMs, maxWaitMs, leading = false, }) {
        this.fn = fn;
        this.invoke = debounce(() => this.flush(), waitMs, {
            leading,
            trailing: true,
            ...(maxWaitMs === undefined ? {} : { maxWait: maxWaitMs }),
        });
    }
    run(input, signal) {
        // Lodash replaces the scheduled arguments but cannot settle the Promise returned to the
        // superseded caller. Settle it explicitly without resetting the maxWaitMs window.
        this.rejectPending('DEBOUNCED_SCHEDULER_CALL_SUPERSEDED');
        return new Promise((resolve, reject) => {
            const pending = {
                input,
                signal,
                resolve,
                reject,
                onAbort: () => {
                    if (this.pending !== pending) {
                        return;
                    }
                    // A superseding engine run aborts its previous signal before scheduling the next input.
                    // Keep lodash's timer alive so that follow-up can retain the original maxWaitMs window.
                    this.rejectPending('DEBOUNCED_SCHEDULER_CALL_ABORTED');
                },
            };
            this.pending = pending;
            signal.addEventListener('abort', pending.onAbort, { once: true });
            if (signal.aborted) {
                pending.onAbort();
                return;
            }
            this.invoke();
        });
    }
    cancel() {
        this.invoke.cancel();
        this.rejectPending('DEBOUNCED_SCHEDULER_CALL_CANCELLED');
    }
    flush() {
        const pending = this.takePending();
        if (pending === undefined) {
            return;
        }
        if (pending.signal.aborted) {
            pending.reject(abortError('DEBOUNCED_SCHEDULER_CALL_ABORTED'));
            return;
        }
        void Promise.resolve()
            .then(() => this.fn(pending.input, pending.signal))
            .then(pending.resolve, pending.reject);
    }
    rejectPending(errorCode) {
        this.takePending()?.reject(abortError(errorCode));
    }
    takePending() {
        const pending = this.pending;
        if (pending === undefined) {
            return undefined;
        }
        this.pending = undefined;
        pending.signal.removeEventListener('abort', pending.onAbort);
        return pending;
    }
}
function abortError(errorCode) {
    const error = new Error(errorCode);
    error.name = 'AbortError';
    return error;
}
//# sourceMappingURL=DebouncedScheduler.js.map